import React, { useState, useRef } from 'react';
import { 
  X, 
  Maximize, 
  CheckCircle, 
  XCircle,
  User,
  Mail,
  DollarSign,
  Ruler,
  Bed,
  Bath,
  Car,
  Trees,
  Umbrella,
  Waves,
  Download
} from 'lucide-react';
import ModalOverlay from '../../../UI/ModalOverlay/ModalOverlay.tsx';
import styles from './AnteProyectoDetailModal.module.scss';
import StatusBadge from '../Shared/StatusBadge.tsx';
import { AnteProyectoValidation } from '../../../../types/backoffice.ts';

interface AnteProyectoDetailModalProps {
  proyecto: AnteProyectoValidation;
  onClose: () => void;
  onValidate: (id: string, isApproved: boolean, reason?: string, adminNote?: string) => void;
  validationLoading: boolean;
}

const AnteProyectoDetailModal: React.FC<AnteProyectoDetailModalProps> = ({
  proyecto,
  onClose,
  onValidate,
  validationLoading
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'technical' | 'specs' | 'budget' | 'files'>('info');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveNote, setApproveNote] = useState('');

  const userName = proyecto.user?.type === 'COMPANY'
    ? proyecto.user.razon_social
    : proyecto.user ? `${proyecto.user.name} ${proyecto.user.last_name || ''}`.trim() : 'Usuario no disponible';

  const images = proyecto.files?.filter(f => !f.external_url && f.url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) || [];
  const documents = proyecto.files?.filter(f => !f.external_url && f.url.match(/\.(pdf|doc|docx|xls|xlsx)$/i)) || [];
  const links = proyecto.files?.filter(f => f.external_url) || [];

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      window.open(fileUrl, '_blank');
    }
  };

  const handleFullscreen = () => {
    if (modalRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        modalRef.current.requestFullscreen();
      }
    }
  };

  const handleApprove = () => {
    if (showApproveForm) {
      onValidate(proyecto.id, true, undefined, approveNote || undefined);
    } else {
      setShowApproveForm(true);
      setShowRejectForm(false);
    }
  };

  const handleReject = () => {
    if (showRejectForm) {
      if (!rejectReason.trim()) {
        alert('Debes proporcionar una razón para el rechazo');
        return;
      }
      onValidate(proyecto.id, false, rejectReason);
    } else {
      setShowRejectForm(true);
      setShowApproveForm(false);
    }
  };

  const tabs = [
    { id: 'info' as const, label: 'Información General' },
    { id: 'technical' as const, label: 'Datos Técnicos' },
    { id: 'specs' as const, label: 'Especificaciones' },
    { id: 'budget' as const, label: 'Presupuesto' },
    { id: 'files' as const, label: `Archivos (${images.length + documents.length + links.length})` }
  ];

  return (
    <ModalOverlay isOpen={true} onClose={onClose} zIndex={1000}>
      <div className={styles.modal} ref={modalRef}>
        <div className={styles.modalContent}>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
          
          <button className={styles.fullscreenButton} onClick={handleFullscreen}>
            <Maximize size={16} />
          </button>

          <div className={styles.modalHeader}>
            <div>
              <h2 className={styles.title}>{proyecto.name}</h2>
              <StatusBadge status={proyecto.state} type="project" />
            </div>
          </div>

          <div className={styles.tabs}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'info' && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Información General</h3>
                
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <User size={24} className={styles.infoIcon} />
                    <div>
                      <div className={styles.infoLabel}>Usuario</div>
                      <div className={styles.infoValue}>{userName}</div>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <Mail size={24} className={styles.infoIcon} />
                    <div>
                      <div className={styles.infoLabel}>Email</div>
                      <div className={styles.infoValue}>{proyecto.user?.email}</div>
                    </div>
                  </div>
                </div>

                <div className={styles.description}>
                  <div className={styles.infoLabel}>Descripción</div>
                  <p>{proyecto.description}</p>
                </div>

                <div className={styles.dates}>
                  <div>
                    <div className={styles.infoLabel}>Fecha de creación</div>
                    <div className={styles.infoValue}>
                      {new Date(proyecto.created_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                  <div>
                    <div className={styles.infoLabel}>Última actualización</div>
                    <div className={styles.infoValue}>
                      {new Date(proyecto.updated_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'technical' && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Datos Técnicos</h3>
                
                <div className={styles.technicalGrid}>
                  <div className={styles.techItem}>
                    <Ruler size={20} className={styles.techIcon} />
                    <div>
                      <div className={styles.infoLabel}>Superficie Total</div>
                      <div className={styles.infoValue}>{parseFloat(proyecto.total_surface || '0').toFixed(2)} m²</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <div>
                      <div className={styles.infoLabel}>Superficie Cubierta</div>
                      <div className={styles.infoValue}>{parseFloat(proyecto.covered_surface || '0').toFixed(2)} m²</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <div>
                      <div className={styles.infoLabel}>Superficie Semicubierta</div>
                      <div className={styles.infoValue}>{parseFloat(proyecto.semicovered_surface || '0').toFixed(2)} m²</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <div>
                      <div className={styles.infoLabel}>Superficie Descubierta</div>
                      <div className={styles.infoValue}>{parseFloat(proyecto.uncovered_surface || '0').toFixed(2)} m²</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <div>
                      <div className={styles.infoLabel}>Ambientes</div>
                      <div className={styles.infoValue}>{proyecto.environments}</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <Bed size={20} className={styles.techIcon} />
                    <div>
                      <div className={styles.infoLabel}>Dormitorios</div>
                      <div className={styles.infoValue}>{proyecto.bedrooms}</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <Bath size={20} className={styles.techIcon} />
                    <div>
                      <div className={styles.infoLabel}>Baños</div>
                      <div className={styles.infoValue}>{proyecto.bathrooms}</div>
                    </div>
                  </div>

                  <div className={styles.techItem}>
                    <div>
                      <div className={styles.infoLabel}>Pisos</div>
                      <div className={styles.infoValue}>{proyecto.floors}</div>
                    </div>
                  </div>
                </div>

                <div className={styles.features}>
                  <div className={styles.infoLabel}>Características</div>
                  <div className={styles.featuresList}>
                    {proyecto.garage && (
                      <div className={styles.feature}>
                        <Car size={16} />
                        <span>Garage</span>
                      </div>
                    )}
                    {proyecto.yard && (
                      <div className={styles.feature}>
                        <Trees size={16} />
                        <span>Patio</span>
                      </div>
                    )}
                    {proyecto.terrace && (
                      <div className={styles.feature}>
                        <Umbrella size={16} />
                        <span>Terraza</span>
                      </div>
                    )}
                    {proyecto.pool && (
                      <div className={styles.feature}>
                        <Waves size={16} />
                        <span>Piscina</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'specs' && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Especificaciones</h3>
                
                <div className={styles.specsGrid}>
                  {proyecto.category && (
                    <div className={styles.specItem}>
                      <div className={styles.infoLabel}>Categoría</div>
                      <div className={styles.specValue}>{proyecto.category.name}</div>
                    </div>
                  )}

                  {proyecto.subcategory && (
                    <div className={styles.specItem}>
                      <div className={styles.infoLabel}>Subcategoría</div>
                      <div className={styles.specValue}>{proyecto.subcategory.name}</div>
                    </div>
                  )}

                  {proyecto.use && (
                    <div className={styles.specItem}>
                      <div className={styles.infoLabel}>Uso</div>
                      <div className={styles.specValue}>{proyecto.use.name}</div>
                    </div>
                  )}

                  {proyecto.materiality && (
                    <div className={styles.specItem}>
                      <div className={styles.infoLabel}>Materialidad</div>
                      <div className={styles.specValue}>{proyecto.materiality.name}</div>
                    </div>
                  )}

                  <div className={styles.specItem}>
                    <div className={styles.infoLabel}>Tipo de Construcción</div>
                    <div className={styles.specValue}>{proyecto.construction_type}</div>
                  </div>

                  <div className={styles.specItem}>
                    <div className={styles.infoLabel}>Etapa</div>
                    <div className={styles.specValue}>{proyecto.stage}</div>
                  </div>

                  {proyecto.implantation && (
                    <div className={styles.specItem}>
                      <div className={styles.infoLabel}>Implantación</div>
                      <div className={styles.specValue}>{proyecto.implantation} m2</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'budget' && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Presupuesto</h3>
                
                <div className={styles.budgetInfo}>
                  <div className={styles.budgetItem}>
                    <DollarSign size={30} className={styles.budgetIcon} />
                    <div>
                      <div className={styles.infoLabel}>Presupuesto de Obra</div>
                      <div className={styles.budgetValue}>
                        {proyecto.currency || 'ARS'} {parseFloat(proyecto.budget || '0').toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {proyecto.consultation_price && (
                    <div className={styles.budgetItem}>
                      <DollarSign size={30} className={styles.budgetIcon} />
                      <div>
                        <div className={styles.infoLabel}>Precio de Consulta</div>
                        <div className={styles.budgetValue}>
                          {proyecto.currency || 'ARS'} {parseFloat(proyecto.consultation_price).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={styles.budgetItem}>
                    <div>
                      <div className={styles.infoLabel}>Moneda</div>
                      <div className={styles.infoValue}>{proyecto.currency || 'ARS'}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'files' && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Archivos y Enlaces</h3>
                
                {images.length > 0 && (
                  <div className={styles.filesSection}>
                    <h4>Imágenes</h4>
                    <div className={styles.imagesGrid}>
                      {images.map((file, index) => (
                        <div key={index} className={styles.imageItem}>
                          <div className={styles.imageWrapper}>
                            <img src={file.url} alt={file.name_file} />
                            <button 
                              className={styles.downloadButton}
                              onClick={() => handleDownload(file.url, file.name_file)}
                              title="Descargar imagen"
                            >
                              <Download size={20} />
                            </button>
                          </div>
                          <div className={styles.imageInfo}>
                            <div className={styles.fileName}>{file.name_file}</div>
                            {file.tag && (
                              <div className={styles.fileTag}>
                                <span className={styles.tagLabel}>Etiqueta:</span>
                                <span className={styles.tagValue}>{file.tag.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {documents.length > 0 && (
                  <div className={styles.filesSection}>
                    <h4>Documentos</h4>
                    <div className={styles.documentsList}>
                      {documents.map((file, index) => (
                        <div key={index} className={styles.documentItem}>
                          <div className={styles.documentInfo}>
                            <div className={styles.fileName}>{file.name_file}</div>
                            {file.tag && (
                              <div className={styles.fileTag}>
                                <span className={styles.tagLabel}>Etiqueta:</span>
                                <span className={styles.tagValue}>{file.tag.name}</span>
                              </div>
                            )}
                          </div>
                          <button 
                            className={styles.downloadDocButton}
                            onClick={() => handleDownload(file.url, file.name_file)}
                          >
                            <Download size={16} />
                            Descargar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {links.length > 0 && (
                  <div className={styles.linksSection}>
                    <h4>Enlaces</h4>
                    <div className={styles.linksList}>
                      {links.map((link, index) => (
                        <div key={index} className={styles.linkItemWrapper}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.linkItem}
                          >
                            {link.name_file || link.url}
                          </a>
                          {link.tag && (
                            <div className={styles.linkTag}>
                              <span className={styles.tagLabel}>Etiqueta:</span>
                              <span className={styles.tagValue}>{link.tag.name}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {images.length === 0 && documents.length === 0 && links.length === 0 && (
                  <div className={styles.noFiles}>
                    No hay archivos adjuntos
                  </div>
                )}
              </div>
            )}
          </div>

          {(!proyecto.state || proyecto.state === '' || proyecto.state === 'pending') && (
            <div className={styles.actions}>
              {!showApproveForm && !showRejectForm && (
                <>
                  <button
                    className={styles.approveButton}
                    onClick={handleApprove}
                    disabled={validationLoading}
                  >
                    <CheckCircle size={16} />
                    Aprobar Proyecto
                  </button>
                  <button
                    className={styles.rejectButton}
                    onClick={handleReject}
                    disabled={validationLoading}
                  >
                    <XCircle size={16} />
                    Rechazar Proyecto
                  </button>
                </>
              )}

              {showApproveForm && (
                <div className={styles.form}>
                  <label>Nota del administrador (opcional):</label>
                  <textarea
                    value={approveNote}
                    onChange={(e) => setApproveNote(e.target.value)}
                    placeholder="Agrega una nota sobre la aprobación..."
                    rows={3}
                  />
                  <div className={styles.formActions}>
                    <button onClick={handleApprove} disabled={validationLoading}>
                      Confirmar Aprobación
                    </button>
                    <button onClick={() => setShowApproveForm(false)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {showRejectForm && (
                <div className={styles.form}>
                  <label>Razón del rechazo (obligatorio):</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explica por qué se rechaza el proyecto..."
                    rows={4}
                    required
                  />
                  <div className={styles.formActions}>
                    <button onClick={handleReject} disabled={validationLoading || !rejectReason.trim()}>
                      Confirmar Rechazo
                    </button>
                    <button onClick={() => setShowRejectForm(false)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
};

export default AnteProyectoDetailModal;

